import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dequal } from 'dequal';
import { tinctApi, useGetLandingPageChatAgentMessages } from '@tinct/core-api';
import { createAiChatEventBus } from '@tinct/web-ai';
import type { ChatMessage } from '@tinct/core-api-models';
import type { AiChatEventBus } from '@tinct/web-ai';
import { createChatStreamHandlers } from '../../../../utils/chat-stream-handlers';

const PAGE_SIZE = 20;

export interface UseLandingPageChatOptions {
  landingPageId: string | null;
  /**
   * Called when a turn was sent with a block context but the stream echoed no
   * `context` block back — the selection didn't resolve server-side (stale
   * id) and the turn ran globally, so the host should clear its selection UI.
   */
  onContextIgnored?: () => void;
}

export interface UseLandingPageChatReturn {
  messages: ChatMessage[] | undefined;
  eventBus: AiChatEventBus;
  isLoadingMore: boolean;
  hasMore: boolean;
  /**
   * `blockId` is the selected core block's id, sent as structured context
   * (`{ block_id }`) — never composed into the message text. The backend
   * resolves it to the block's data-lp path. Null/undefined = global ask.
   */
  handleSendMessage: (message: string, blockId?: string | null) => void;
  handleLoadMore: () => void;
}

/**
 * The hosted landing page's edit chat — the single edit path. Same transport,
 * event protocol, and pagination as the campaign configure chat; the
 * conversation is created lazily on the first message, so an empty history is
 * the expected first-visit state (the client shows static opener copy).
 */
export function useLandingPageChat({
  landingPageId,
  onContextIgnored,
}: UseLandingPageChatOptions): UseLandingPageChatReturn {
  const eventBus = useMemo(() => createAiChatEventBus(), []);

  // Ref so `handleSendMessage` keeps a stable identity across renders.
  const onContextIgnoredRef = useRef(onContextIgnored);
  useEffect(() => {
    onContextIgnoredRef.current = onContextIgnored;
  }, [onContextIgnored]);

  // ==========================================================================
  // Message state
  // ==========================================================================

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // ==========================================================================
  // Fetch initial messages (newest first — backend returns created DESC)
  // ==========================================================================

  const { data: initialData, isFetched } = useGetLandingPageChatAgentMessages(
    {
      staticParams: { landingPageId: landingPageId ?? '' },
      offset: 0,
      limit: PAGE_SIZE,
    },
    {
      enabled: !!landingPageId,
      refetchOnMount: 'always',
      refetchOnReconnect: true,
    }
  );

  // Derive state during render (React-approved pattern).
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevData, setPrevData] = useState<ChatMessage[]>();
  if (initialData && !dequal(initialData.data, prevData)) {
    setPrevData(initialData.data);

    const total = initialData.pagination.count;
    if (total > 0) {
      setMessages([...initialData.data].reverse());
      setTotalCount(total);
      setNextOffset(PAGE_SIZE);
      setHasMore(total > PAGE_SIZE);
    }
  }

  // ==========================================================================
  // Load more (older messages — next page in DESC order)
  // ==========================================================================

  const handleLoadMore = useCallback(() => {
    if (!landingPageId || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    tinctApi.landingPages.getLandingPageChatAgentMessages({
      staticParams: { landingPageId },
      offset: nextOffset,
      limit: PAGE_SIZE,
    }).then((response) => {
      setMessages((prev) => [...[...response.data].reverse(), ...prev]);
      const newOffset = nextOffset + response.data.length;
      setNextOffset(newOffset);
      setHasMore(newOffset < totalCount);
      setIsLoadingMore(false);
    }).catch(() => {
      setIsLoadingMore(false);
    });
  }, [landingPageId, isLoadingMore, hasMore, nextOffset, totalCount]);

  // ==========================================================================
  // Send message
  // ==========================================================================

  const handleSendMessage = useCallback((message: string, blockId?: string | null) => {
    if (!landingPageId) return;
    const { onEvent, onError, onComplete } = createChatStreamHandlers(eventBus, {
      // Only meaningful on a scoped turn: no context echo back means the
      // selection was ignored server-side.
      onContextOutcome: blockId
        ? (echoed) => {
            if (!echoed) onContextIgnoredRef.current?.();
          }
        : undefined,
    });
    tinctApi.landingPages.postLandingPageMessageChat(
      landingPageId,
      message,
      blockId ? { blockId } : null,
      onEvent,
      onError,
      onComplete
    );
  }, [landingPageId, eventBus]);

  return {
    messages: isFetched ? messages : undefined,
    eventBus,
    isLoadingMore,
    hasMore,
    handleSendMessage,
    handleLoadMore,
  };
}
